import React, { useEffect, useMemo, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { usePeriod } from "@/contexts/PeriodContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import { eur, eurKompakt, num } from "@/lib/format";
import { toast } from "sonner";
import ImportWizard from "@/components/ImportWizard";
import {
    ArrowDownRight,
    ArrowUpRight,
    AlertTriangle,
    PackageSearch,
    FileWarning,
    BookOpenCheck,
} from "lucide-react";

function KPI({ label, value, hint, intent = "neutral", testid, fullValue, details }) {
    const tone =
        intent === "positive"
            ? "text-emerald-400"
            : intent === "warning"
            ? "text-yellow-400"
            : intent === "critical"
            ? "text-red-400"
            : "text-foreground";
    return (
        <Card className="p-5 bg-surface1 border-border hover:border-tenant transition-colors min-w-0" data-testid={testid}>
            <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground leading-tight min-h-[2em]">
                {label}
            </div>
            <div
                className={`mt-2 font-heading text-3xl tracking-tight font-semibold tabular ${tone}`}
                title={fullValue || (typeof value === "string" ? value : undefined)}
            >
                {value}
            </div>
            {details && details.length > 0 && (
                <div className="mt-3 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
                    {details.map((d, i) => (
                        <div key={i}>
                            {d.label}: <span className="tabular">{d.value}</span>
                        </div>
                    ))}
                </div>
            )}
            {hint && !details && <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{hint}</div>}
        </Card>
    );
}

// Pastell-Palette für den Erlösgruppen-Donut (zart, passt zu hellgrau)
const DONUT_COLORS = ["#7DD3FC", "#A78BFA", "#FCA5A5", "#86EFAC", "#FCD34D", "#F9A8D4", "#FDBA74"];

function ErloesgruppenDonut({ data }) {
    if (!data || data.length === 0) {
        return <div className="mt-3 text-xs text-muted-foreground italic">Keine Daten</div>;
    }
    const total = data.reduce((s, e) => s + e.bilanzwert, 0);
    return (
        <div className="mt-3 flex flex-col items-stretch gap-3">
            {/* Donut zentriert oben */}
            <div className="mx-auto w-[96px] h-[96px]">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="bilanzwert"
                            nameKey="erloesgruppe"
                            innerRadius={26}
                            outerRadius={46}
                            paddingAngle={2}
                            stroke="none"
                            isAnimationActive={false}
                        >
                            {data.map((_, i) => (
                                <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            </div>
            {/* Legende untereinander */}
            <ul className="space-y-2 text-xs text-muted-foreground">
                {data.map((e, i) => {
                    const pct = total > 0 ? (e.bilanzwert / total) * 100 : 0;
                    return (
                        <li key={i} className="min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span
                                    className="h-2 w-2 rounded-sm flex-shrink-0"
                                    style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}
                                />
                                <span className="truncate" title={e.erloesgruppe}>
                                    {e.erloesgruppe}
                                </span>
                            </div>
                            <div className="ml-3.5 tabular whitespace-nowrap">
                                {eur(e.bilanzwert)}
                                <span className="opacity-70"> · {pct.toFixed(1)} %</span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default function Dashboard() {
    const { activeTenant } = useTenant();
    const { zeitraum, setZeitraum } = usePeriod();
    const [stats, setStats] = useState(null);
    const [verlauf, setVerlauf] = useState({ granularitaet: "jahr", series: [] });
    const [granularitaet, setGranularitaet] = useState("jahr");
    const [quality, setQuality] = useState(null);
    const [wizardOpen, setWizardOpen] = useState(false);

    const load = async () => {
        if (!activeTenant) return;
        try {
            const [s, v, q] = await Promise.all([
                api.get(`/dashboard/stats`, { params: { tenant_id: activeTenant.tenant_id, zeitraum } }),
                api.get(`/dashboard/verlauf`, { params: { tenant_id: activeTenant.tenant_id, granularitaet } }),
                api.get(`/dashboard/datenqualitaet`, { params: { tenant_id: activeTenant.tenant_id, zeitraum } }),
            ]);
            setStats(s.data);
            setVerlauf(v.data);
            setQuality(q.data);
        } catch (e) {
            toast.error("Daten konnten nicht geladen werden");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id, granularitaet, zeitraum]);

    const accent = activeTenant?.color || "#2979FF";

    const deltaPositive = (stats?.delta_eur || 0) >= 0;

    return (
        <div className="space-y-8 pb-24 animate-fade-up">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Übersicht</div>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">
                        Inventurbewertung
                        <span className="text-tenant ml-3 sm:ml-4">{activeTenant?.name}</span>
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Bewertungszeitraum: <span className="font-medium text-foreground">{zeitraum}</span>
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={zeitraum}
                        onChange={(e) => setZeitraum(e.target.value)}
                        data-testid="zeitraum-select"
                        className="h-10 px-3 rounded-md border border-border bg-surface1 text-sm"
                    >
                        {["2024", "2025", "2026"].map((y) => (
                            <option key={y} value={y}>
                                Jahr {y}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <KPI
                    label="Gesamtlagerwert"
                    value={eurKompakt(stats?.gesamt_lagerwert)}
                    fullValue={eur(stats?.gesamt_lagerwert)}
                    details={[
                        { label: "Genau", value: eur(stats?.gesamt_lagerwert) },
                        { label: "Aktive Titel", value: num(stats?.aktive_titel || 0) },
                    ]}
                    testid="kpi-lagerwert"
                />
                <KPI
                    label="Bilanzwert"
                    value={eurKompakt(stats?.gesamt_bilanzwert)}
                    fullValue={eur(stats?.gesamt_bilanzwert)}
                    details={[
                        { label: "Genau", value: eur(stats?.gesamt_bilanzwert) },
                        { label: "WB", value: eur(stats?.wertberichtigung) },
                    ]}
                    intent="positive"
                    testid="kpi-bilanzwert"
                />
                {/* Bestandsveränderung: Bilanzwert(N) − Bilanzwert(N-1) */}
                <Card
                    className="p-5 bg-surface1 border-border hover:border-tenant transition-colors min-w-0"
                    data-testid="kpi-bestandsveraenderung"
                >
                    <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground leading-tight min-h-[2em]">
                        Bestandsveränderung
                    </div>
                    {(() => {
                        const delta = stats?.bestand_delta_eur || 0;
                        const hasCurrent = (stats?.gesamt_bilanzwert || 0) > 0;
                        const hasPrev = (stats?.vorjahr_bilanzwert || 0) > 0;
                        // Vergleich nur, wenn BEIDE Jahre Daten haben (sonst irreführende Riesensummen)
                        const canCompare = hasCurrent && hasPrev;
                        const effDelta = canCompare ? delta : 0;
                        const effPct = canCompare ? stats?.bestand_delta_pct : 0;
                        const positive = effDelta >= 0;
                        const sign = effDelta === 0 ? "" : (positive ? "+" : "−");
                        return (
                            <>
                                <div className="mt-1 flex items-baseline flex-wrap gap-x-2 gap-y-0.5">
                                    <div
                                        className="font-heading text-xl xl:text-2xl font-bold tracking-tight tabular whitespace-nowrap text-amber-400"
                                        title={`Δ Bilanzwert ${zeitraum} vs. ${parseInt(zeitraum, 10) - 1}`}
                                    >
                                        {sign}{eur(Math.abs(effDelta))}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground/80">
                                        {canCompare
                                            ? (effDelta === 0
                                                ? "unverändert"
                                                : (positive ? "Bestandserhöhung" : "Bestandsminderung"))
                                            : "kein Vergleichsjahr"}
                                        {canCompare && effPct != null && (
                                            <> · <span className="text-white font-medium">{sign}{num(Math.abs(effPct))} %</span></>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-3 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
                                    <div>Bilanz {zeitraum}: <span className="tabular text-foreground/80">{hasCurrent ? eur(stats?.gesamt_bilanzwert) : "—"}</span></div>
                                    <div>Bilanz {parseInt(zeitraum, 10) - 1}: <span className="tabular">{hasPrev ? eur(stats?.vorjahr_bilanzwert) : "—"}</span></div>
                                </div>
                            </>
                        );
                    })()}
                </Card>
                {/* Vorjahresvergleich (Fokus: Prozentwert) */}
                <Card
                    className="p-5 bg-surface1 border-border hover:border-tenant transition-colors min-w-0"
                    data-testid="kpi-vorjahresvergleich"
                >
                    <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground leading-tight min-h-[2em]">
                        Vorjahresvergleich
                    </div>
                    {(() => {
                        const hasCurrent = (stats?.gesamt_lagerwert || 0) > 0;
                        const hasPrev = (stats?.vorjahr_lagerwert || 0) > 0;
                        const canCompare = hasCurrent && hasPrev;
                        const pct = canCompare ? (stats?.delta_pct || 0) : 0;
                        const positive = pct >= 0;
                        return (
                            <>
                                <div
                                    className={`mt-2 font-heading text-4xl xl:text-5xl font-bold tracking-tight tabular whitespace-nowrap inline-flex items-center gap-2 ${
                                        positive ? "text-emerald-400" : "text-red-400"
                                    }`}
                                    title={`Δ ${eur(stats?.delta_eur)}`}
                                >
                                    {canCompare && (positive ? <ArrowUpRight className="h-7 w-7" /> : <ArrowDownRight className="h-7 w-7" />)}
                                    {num(pct)} %
                                </div>
                                <div className="mt-3 space-y-1 border-t border-border pt-3 text-xs text-muted-foreground">
                                    <div>Aktuell: <span className="tabular text-foreground/80">{hasCurrent ? eur(stats?.gesamt_lagerwert) : "—"}</span></div>
                                    <div>Vorjahr: <span className="tabular">{hasPrev ? eur(stats?.vorjahr_lagerwert) : "—"}</span></div>
                                    <div>Δ absolut: <span className="tabular">{canCompare ? eur(stats?.delta_eur) : "—"}</span></div>
                                </div>
                            </>
                        );
                    })()}
                </Card>
            </div>

            {/* Datenqualität-Kachel */}
            <Card className="p-5 bg-surface1 border-border" data-testid="datenqualitaet-card">
                <div className="flex items-center gap-2 mb-4">
                    <FileWarning className="h-4 w-4 text-tenant" />
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                            Datenqualität
                        </div>
                        <div className="text-sm text-foreground">
                            Zeitraum <span className="font-medium">{zeitraum}</span>
                            {quality?.et_fenster && (
                                <span className="text-muted-foreground"> · ET-Fenster {quality.et_fenster.join("–")}</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div data-testid="dq-ohne-hk" className="p-4 rounded-md bg-surface2 border border-border">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                Ohne HK-Wert
                            </div>
                            <AlertTriangle className={`h-4 w-4 ${quality?.ohne_hk > 0 ? "text-yellow-400" : "text-muted-foreground"}`} />
                        </div>
                        <div className={`mt-2 font-heading text-3xl tabular ${quality?.ohne_hk > 0 ? "text-yellow-400" : "text-foreground"}`}>
                            {num(quality?.ohne_hk || 0)}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                            Bewegungen ohne HK Ist und HK Kal
                        </div>
                        {quality?.hk_bestaetigt_leer > 0 && (
                            <div
                                className="text-[11px] text-emerald-400 mt-1"
                                data-testid="dq-hk-bestaetigt-leer"
                                title="Titel mit hk_confirmed_null=true zählen nicht als Fehler"
                            >
                                + {num(quality.hk_bestaetigt_leer)} als HK bewusst leer bestätigt (ausgeblendet)
                            </div>
                        )}
                    </div>
                    <div data-testid="dq-datenleichen" className="p-4 rounded-md bg-surface2 border border-border">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                Datenleichen
                            </div>
                            <PackageSearch className={`h-4 w-4 ${quality?.datenleichen > 0 ? "text-orange-400" : "text-muted-foreground"}`} />
                        </div>
                        <div className={`mt-2 font-heading text-3xl tabular ${quality?.datenleichen > 0 ? "text-orange-400" : "text-foreground"}`}>
                            {num(quality?.datenleichen || 0)}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                            Inaktive Titel (kein Bestand, ET veraltet)
                        </div>
                    </div>
                    <div data-testid="dq-vorschau-null" className="p-4 rounded-md bg-surface2 border border-border">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground">
                                Vorschau 0-Lager
                            </div>
                            <BookOpenCheck className={`h-4 w-4 ${quality?.vorschau_null > 0 ? "text-sky-400" : "text-muted-foreground"}`} />
                        </div>
                        <div className={`mt-2 font-heading text-3xl tabular ${quality?.vorschau_null > 0 ? "text-sky-400" : "text-foreground"}`}>
                            {num(quality?.vorschau_null || 0)}
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-1">
                            0-Bestand, ET im Fenster (frisch / anstehend)
                        </div>
                    </div>
                </div>
            </Card>

            <div className="grid lg:grid-cols-3 gap-5">
                <Card className="lg:col-span-2 p-6 bg-surface1 border-border">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Inventarwert-Verlauf</div>
                            <h3 className="font-heading text-xl font-semibold mt-0.5">Lager- und Bilanzwert</h3>
                        </div>
                        <Tabs value={granularitaet} onValueChange={setGranularitaet}>
                            <TabsList data-testid="verlauf-tabs">
                                <TabsTrigger value="monat">Monat</TabsTrigger>
                                <TabsTrigger value="quartal">Quartal</TabsTrigger>
                                <TabsTrigger value="jahr">Jahr</TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={verlauf.series}>
                                <defs>
                                    <linearGradient id="gradLager" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={accent} stopOpacity={0.55} />
                                        <stop offset="100%" stopColor={accent} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.08)" />
                                <XAxis dataKey="zeitraum" stroke="rgba(255,255,255,0.5)" fontSize={12} />
                                <YAxis
                                    stroke="rgba(255,255,255,0.5)"
                                    fontSize={12}
                                    tickFormatter={(v) => `${Math.round(v / 1000)}k`}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: "#1A1A2E",
                                        border: "1px solid #31314D",
                                        borderRadius: 8,
                                        color: "#F8F9FA",
                                    }}
                                    formatter={(v) => eur(v)}
                                />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Area
                                    type="monotone"
                                    dataKey="lagerwert"
                                    name="Lagerwert"
                                    stroke={accent}
                                    fill="url(#gradLager)"
                                    strokeWidth={2}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="bilanzwert"
                                    name="Bilanzwert"
                                    stroke="#22C55E"
                                    fillOpacity={0}
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="p-6 bg-surface1 border-border min-w-0" data-testid="kpi-erloesgruppen-large">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Verteilung</div>
                    <h3 className="font-heading text-xl font-semibold mt-0.5">Bilanzwert je Erlösgruppe</h3>
                    <ErloesgruppenDonut data={stats?.bilanzwert_je_erloesgruppe || []} />
                </Card>
            </div>

            <ImportWizard open={wizardOpen} onOpenChange={setWizardOpen} onImported={load} />
        </div>
    );
}
